import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Cancel01Icon } from 'hugeicons-react';
import '../../Styles/Partials/SearchModal.css';
import { useNavigate } from 'react-router-dom';
import { useChatContext, useUnifiedData } from '../../contexts/AppDataContext';
import { useWindow } from '../../contexts/WindowContext';
import { useInputContext } from '../../contexts/InputContext';
import { useToast } from '../../contexts/ToastContext';
import { useQuery } from "convex/react";
import { useAuth } from '../../contexts/AuthContext';
import { MinimizedMessage } from '../../types/message';
import { SearchResult } from '../../types/search';
import { SearchSkeleton } from './SearchSkeleton';
import { SearchEmptyState, SearchInitialState } from './SearchEmptyState';
import { SearchResults } from './SearchResults';
import { getHighlightedPreview } from './utils';
import { useAnalytics } from '../../hooks/useAnalytics';

export const SearchModal: React.FC = () => {
  const { isSearchModalOpen, setIsSearchModalOpen, setMessages, setMemoizedChatTitle } = useChatContext();
  const { setInput, focusInput } = useInputContext();
  const { user } = useAuth();
  const toast = useToast();
  const { chats } = useUnifiedData();
  const { trackError, trackSearch } = useAnalytics();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [displayedResults, setDisplayedResults] = useState<SearchResult[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(-1);
  
  // Replace searchTimeout state with ref
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentSearchIdRef = useRef<string>('');
  
  const resultsPerPage = 10;
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultListRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement | null>(null);
  
  const navigate = useNavigate();
  const { windowWidth } = useWindow();
  
  // Convex search query - only runs when we have a debounced query and user ID
  const convexSearchResults: MinimizedMessage[] | undefined = useQuery(
    "messages:searchMessages" as any,
    debouncedQuery.trim() && user?._id ? { query: debouncedQuery.trim(), userId: user._id, legacyUserId: user.legacyId } : "skip"
  );

  const handleAddChat = useCallback(async () => {
    try {
      setMessages([]);
      setInput('');
      setMemoizedChatTitle('New Chat');
      navigate('/', { replace: false });
      if(windowWidth > 768) {
        setTimeout(() => {
          focusInput();
        }, 100);
      }
    } catch (error) {
      toast('Failed to create a new chat. Please try again.', 'error');
    }
  }, [navigate, toast, setMessages, setInput, setMemoizedChatTitle, focusInput, windowWidth]);

  const isMobile = windowWidth < 768;
  const hasSearchQuery = searchQuery.trim().length > 0;

  // Reset search when modal opens/closes
  useEffect(() => {
    if (isSearchModalOpen) {
      // Focus input when modal opens with a slight delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      // Clear search when closing
      setSearchQuery('');
      setDebouncedQuery('');
      setSearchResults([]);
      setDisplayedResults([]);
      setCurrentPage(1);
      setIsSearching(false);
      setSelectedResultIndex(-1);
    }
  }, [isSearchModalOpen]);

  const closeSearchModal = useCallback(() => {
    setIsSearchModalOpen(false);
  }, [setIsSearchModalOpen]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeSearchModal();
      }
    };

    if (isSearchModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchModalOpen, closeSearchModal]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSearchModalOpen) {
        closeSearchModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isSearchModalOpen, closeSearchModal]);

  // Handle keyboard navigation using React state instead of DOM manipulation
  useEffect(() => {
    const handleKeyNavigation = (e: KeyboardEvent) => {
      if (!isSearchModalOpen || !hasSearchQuery) return;
      
      const isArrowUp = e.key === 'ArrowUp';
      const isArrowDown = e.key === 'ArrowDown';
      const isEnter = e.key === 'Enter';
      
      if (isArrowUp || isArrowDown) {
        e.preventDefault();
        
        if (displayedResults.length === 0) return;
        
        let newIndex = selectedResultIndex;
        
        if (isArrowDown) {
          newIndex = Math.min(selectedResultIndex + 1, displayedResults.length - 1);
        } else if (isArrowUp) {
          newIndex = Math.max(selectedResultIndex - 1, 0);
        }
        
        setSelectedResultIndex(newIndex);
        
        // Scroll the selected item into view
        setTimeout(() => {
          const selectedItem = document.querySelector('.search-result-item.keyboard-selected');
          if (selectedItem) {
            selectedItem.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' // Change to 'center' for better visibility
            });
          }
        }, 0);
      } 
      
      if (isEnter && selectedResultIndex >= 0) {
        e.preventDefault();
        
        // Handle enter key by clicking the selected result
        if (displayedResults[selectedResultIndex]) {
          handleResultClick(displayedResults[selectedResultIndex]);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyNavigation);
    return () => {
      document.removeEventListener('keydown', handleKeyNavigation);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchModalOpen, hasSearchQuery, displayedResults, selectedResultIndex]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const query = inputValue.trim();

    if (!query) {
      setDebouncedQuery('');
      setSearchResults([]);
      setDisplayedResults([]);
      setCurrentPage(1);
      setIsSearching(false);
      setSelectedResultIndex(-1);
      return;
    }

    // Set searching state immediately when user types valid query
    setIsSearching(true);
    
    // Generate a new search ID to track this search
    const searchId = Date.now().toString();
    currentSearchIdRef.current = searchId;
    
    // Debounce the search with a 300ms delay
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
  };

  // Process Convex search results
  useEffect(() => {
    try {
      // When convexSearchResults is defined (even if empty), we're done searching
      if (convexSearchResults !== undefined) {
        setIsSearching(false);
      }
      
      if (convexSearchResults instanceof Error) {
        // Handle search errors
        setSearchResults([]);
        setDisplayedResults([]);
        setCurrentPage(1);
        
        // Analytics for search errors
        trackError(new Error(convexSearchResults.message), 'search', {
          query: debouncedQuery,
        });
        return;
      }

      // Get the current search ID associated with these results
      const searchId = currentSearchIdRef.current;
      
      // If search ID is empty, ignore these results
      // This prevents stale results from overwriting newer ones
      if (!searchId) {
        return;
      }

      if (convexSearchResults && Array.isArray(convexSearchResults)) {
        const processedResults = convexSearchResults.map(message => {
          // Find chat info for this message
          const chatInfo = chats?.find((chat: any) => chat.id === message.chatId);
          const chatTitle = chatInfo?.title || 'Untitled Chat';
          
          // Get preview with highlighted match
          const preview = getHighlightedPreview(message.content, debouncedQuery);
          
          return {
            id: message.id,
            chat_id: message.chatId,
            content: message.content,
            isAi: false, // MinimizedMessage doesn't have isAi, default to false
            timestamp: message.timestamp,
            messagePairId: '', // MinimizedMessage doesn't have messagePairId
            model: '', // MinimizedMessage doesn't have model
            chatId: message.chatId,
            chatTitle,
            preview
          };
        });

        const sortedResults = processedResults.sort((a, b) => 
          new Date(b.timestamp || Date.now()).getTime() - new Date(a.timestamp || Date.now()).getTime()
        );

        // Reset selected index when getting new results
        setSelectedResultIndex(-1);
        
        setSearchResults(sortedResults);
        setDisplayedResults(sortedResults.slice(0, resultsPerPage));
        setCurrentPage(1);
        
        // Analytics for successful search
        trackSearch(debouncedQuery, sortedResults.length);
      } else if (debouncedQuery && convexSearchResults !== undefined) {
        // No results found
        setSearchResults([]);
        setDisplayedResults([]);
        setCurrentPage(1);
      }
    } catch (error) {
      setIsSearching(false);
      setSearchResults([]);
      setDisplayedResults([]);
      setCurrentPage(1);
    }
  }, [convexSearchResults, chats, debouncedQuery, trackError, trackSearch]);

  // Load more results when scrolling - define this before the observer effect
  const loadMoreResults = useCallback(() => {
    if (isLoadingMore || displayedResults.length >= searchResults.length) return;
    
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * resultsPerPage;
    const endIndex = startIndex + resultsPerPage;
    const newResults = searchResults.slice(startIndex, endIndex);
    
    setTimeout(() => {
      setDisplayedResults(prev => [...prev, ...newResults]);
      setCurrentPage(nextPage);
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore, displayedResults.length, currentPage, resultsPerPage, searchResults]);

  // Setup intersection observer for infinite scrolling
  useEffect(() => {
    // Only setup observer if we have search results and there are more to load
    if (!hasSearchQuery || !searchResults.length || displayedResults.length >= searchResults.length) {
      return;
    }

    // Create new observer with consistent options
    const options = {
      root: resultListRef.current,
      rootMargin: '100px',
      threshold: 0.1
    };
    
    // Define handleObserver inside the effect but using the memoized callback
    const handleObserver = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry && entry.isIntersecting && !isLoadingMore) {
        loadMoreResults();
      }
    };

    // Cleanup previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    observerRef.current = new IntersectionObserver(handleObserver, options);
    
    // Observe the loading element if it exists
    if (loadingRef.current) {
      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  // Only recreate the observer when needed
  }, [hasSearchQuery, searchResults.length, displayedResults.length, isLoadingMore, loadMoreResults]);

  const handleResultClick = useCallback((result: SearchResult) => {
    closeSearchModal();
    navigate(`/${result.chatId}`);
  }, [closeSearchModal, navigate]);

  const handleNewChat = useCallback(() => {
    closeSearchModal();
    handleAddChat();
  }, [closeSearchModal, handleAddChat]);

  // Memoize the results component for better performance
  const resultsComponent = useMemo(() => {
    // Show loading when either:
    // 1. We're actively searching (user is typing)
    // 2. We have a debounced query but no results yet (waiting for API)
    const isLoading = isSearching || (debouncedQuery && convexSearchResults === undefined);
    
    if (isLoading) {
      return <SearchSkeleton isMobileView={isMobile} />;
    }
    
    if (hasSearchQuery) {
      return displayedResults.length > 0 
        ? (
          <SearchResults
            results={displayedResults}
            searchQuery={searchQuery}
            isLoadingMore={isLoadingMore}
            hasMoreResults={displayedResults.length < searchResults.length}
            onResultClick={handleResultClick}
            loadingRef={loadingRef}
            selectedIndex={selectedResultIndex}
          />
        )
        : <SearchEmptyState />;
    } 
    
    return <SearchInitialState onNewChat={handleNewChat} />;
  }, [
    isSearching, debouncedQuery, convexSearchResults, 
    hasSearchQuery, displayedResults, searchResults.length, 
    searchQuery, isLoadingMore, handleResultClick, handleNewChat, isMobile,
    selectedResultIndex
  ]);

  if (!isSearchModalOpen) {
    return null;
  }

  const searchMobileOverlay = () => (
    <div className='search-mobile-overlay'>
      <div className='search-mobile-overlay-header'>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search messages"
          value={searchQuery}
          onChange={handleSearch}
          aria-label="Search messages"
          aria-controls="search-results-list"
          aria-activedescendant={selectedResultIndex >= 0 ? `search-result-${selectedResultIndex}` : undefined}
        />
        <button onClick={closeSearchModal} className="search-close-button">
          <Cancel01Icon size={20} />
        </button>
      </div>
      <div className="search-results-container">
        <div 
          className="results-list" 
          ref={resultListRef}
          id="search-results-list"
          role="listbox"
          aria-label="Search results">
          {resultsComponent}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? (
        searchMobileOverlay()
      ) : (
        <div className={`search-modal ${isSearchModalOpen ? 'open' : ''}`} ref={modalRef}>
          <div className="search-header">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search through your conversations"
              value={searchQuery}
              onChange={handleSearch}
              aria-label="Search messages"
              aria-controls="search-results-list"
              aria-activedescendant={selectedResultIndex >= 0 ? `search-result-${selectedResultIndex}` : undefined}
            />
            {windowWidth < 768 && (
              <button onClick={closeSearchModal} className="close-button">
                <Cancel01Icon size={20} />
              </button>
            )}
          </div>
          <div className="search-results-container">
            <div 
              className="results-list" 
              ref={resultListRef}
              id="search-results-list"
              role="listbox"
              aria-label="Search results">
              {resultsComponent}
            </div>
          </div>
        </div>
      )}
    </>
  );
};